// filepath: c:\Users\Admin\Documents\GitHub\kos-v3-bolt\src\utils\invoice.ts
import { Resident, PaymentPeriod } from '../types';

export interface BankAccount {
  bankName: string;
  accountNumber: string;
  accountHolder: string;
}

export const bankAccounts: BankAccount[] = [
  {
    bankName: 'BRI',
    accountNumber: '0058 0109 9983 501',
    accountHolder: 'Furqanul Hamdi'
  },
  {
    bankName: 'BNI',
    accountNumber: '0340663119',
    accountHolder: 'Furqanul Hamdi'
  },
  {
    bankName: 'Bank Nagari',
    accountNumber: '10080207008443',
    accountHolder: 'Furqanul Hamdi'
  },
  {
    bankName: 'Bank Mandiri',
    accountNumber: '1110022570846',
    accountHolder: 'Furqanul Hamdi'
  },
  {
    bankName: 'BCA/blu',
    accountNumber: '0097 0028 8116',
    accountHolder: 'Furqanul Hamdi'
  },
  {
    bankName: 'Dana/Gopay',
    accountNumber: '08116653299',
    accountHolder: 'Furqanul Hamdi'
  }
];

// Constants for invoice
const COST_PER_MONTH = 700000; // Rp 700.000 per month

export type InvoiceStatus = 'TEPAT_WAKTU' | 'MENUNGGU' | 'TERLAMBAT';

export function calculateInvoiceAmount(resident: Resident): {
  rentAmount: number;
  totalAmount: number;
  daysOverdue: number;
  status: InvoiceStatus;
} {
  // Gunakan tanggal hari ini sebagai patokan untuk perhitungan keterlambatan
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set ke awal hari untuk perbandingan yang konsisten
  
  // Tentukan tanggal jatuh tempo yang benar
  let correctDueDate = '';
  
  // Jika penghuni memiliki periode pembayaran, gunakan nextPaymentDate dari periode terakhir
  if (resident.paymentPeriods && resident.paymentPeriods.length > 0) {
    // Cari periode pembayaran terakhir
    let latestPeriod = resident.paymentPeriods[0];
    resident.paymentPeriods.forEach(period => {
      if (period.periodNumber > latestPeriod.periodNumber) {
        latestPeriod = period;
      }
    });
    
    // Jika kategori terakhir adalah DP, tidak dianggap tertunggak
    if (latestPeriod.category === 'DP') {
      return {
        rentAmount: COST_PER_MONTH * resident.paymentPattern,
        totalAmount: COST_PER_MONTH * resident.paymentPattern,
        daysOverdue: 0,
        status: 'MENUNGGU'
      };
    }
    
    // Gunakan nextPaymentDate dari periode terakhir sebagai tanggal yang benar
    if (latestPeriod.nextPaymentDate && latestPeriod.nextPaymentDate.trim()) {
      correctDueDate = latestPeriod.nextPaymentDate;
    }
  } else {
    // Jika tidak ada periode pembayaran, gunakan nextPaymentDate dari resident
    correctDueDate = resident.nextPaymentDate;
  }
  
  if (!correctDueDate) {
    return {
      rentAmount: COST_PER_MONTH * resident.paymentPattern,
      totalAmount: COST_PER_MONTH * resident.paymentPattern,
      daysOverdue: 0,
      status: 'MENUNGGU'
    };
  }
  
  const dueDate = new Date(correctDueDate);
  dueDate.setHours(0, 0, 0, 0);
  
  // Hitung hari keterlambatan berdasarkan hari ini
  const timeDiff = today.getTime() - dueDate.getTime();
  const daysOverdue = Math.floor(timeDiff / (1000 * 3600 * 24));
  
  // Tentukan status berdasarkan perbandingan dengan hari ini
  let status: InvoiceStatus;
  if (timeDiff < 0) {
    status = 'MENUNGGU';
  } else if (timeDiff === 0) {
    status = 'TEPAT_WAKTU';
  } else {
    status = 'TERLAMBAT';
  }

  const rentAmount = COST_PER_MONTH * resident.paymentPattern;
  const totalAmount = rentAmount;

  return {
    rentAmount,
    totalAmount,
    daysOverdue: Math.max(0, daysOverdue),
    status
  };
}

export function generateInvoiceHTML(resident: Resident): string {
  const { rentAmount, totalAmount, daysOverdue, status } = calculateInvoiceAmount(resident);
  
  // Tentukan tanggal jatuh tempo yang benar untuk ditampilkan
  let correctDueDate = '';
  
  // Jika penghuni memiliki periode pembayaran, gunakan nextPaymentDate dari periode terakhir
  if (resident.paymentPeriods && resident.paymentPeriods.length > 0) {
    // Cari periode pembayaran terakhir
    let latestPeriod = resident.paymentPeriods[0];
    resident.paymentPeriods.forEach(period => {
      if (period.periodNumber > latestPeriod.periodNumber) {
        latestPeriod = period;
      }
    });
    
    // Gunakan nextPaymentDate dari periode terakhir sebagai tanggal yang benar
    if (latestPeriod.nextPaymentDate && latestPeriod.nextPaymentDate.trim()) {
      correctDueDate = latestPeriod.nextPaymentDate;
    }
  } else {
    // Jika tidak ada periode pembayaran, gunakan nextPaymentDate dari resident
    correctDueDate = resident.nextPaymentDate;
  }
  
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', { 
      style: 'currency', 
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  
  const today = new Date();
  today.setFullYear(2025, 3, 22); // April 22, 2025
  
  const invoiceDate = today.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  
  // Generate a unique invoice number
  const invoiceNumber = `INV-${resident.roomNumber}-${today.getFullYear()}${(today.getMonth() + 1).toString().padStart(2, '0')}${today.getDate().toString().padStart(2, '0')}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;
  
  // Split bank accounts into two columns
  const firstColumnAccounts = bankAccounts.slice(0, 3);
  const secondColumnAccounts = bankAccounts.slice(3, 6);
  
  // Generate status display classes and text
  let statusClass = '';
  let statusText = '';
  
  switch(status) {
    case 'TERLAMBAT':
      statusClass = 'status-overdue';
      statusText = `Terlambat: ${daysOverdue} hari`;
      break;
    case 'MENUNGGU':
      statusClass = 'status-pending';
      statusText = `Jatuh Tempo: ${Math.abs(daysOverdue)} hari lagi`;
      break;
    case 'TEPAT_WAKTU':
      statusClass = 'status-on-time';
      statusText = 'Jatuh Tempo: Hari ini';
      break;
  }
  
  // Generate payment notification message based on status
  let paymentNotification = '';
  if (status === 'TERLAMBAT' || status === 'TEPAT_WAKTU') {
    paymentNotification = `
      <div class="payment-notification">
        <p>Yth. ${resident.name},</p>
        <p>Sesuai dengan pola bayar sewa per ${resident.paymentPattern} bulan, kami informasikan bahwa pembayaran untuk periode sewa yang tercantum di bawah ini telah melewati tanggal jatuh tempo pada ${formatDate(correctDueDate)}. Mohon untuk segera menyelesaikan pembayaran.</p>
      </div>
    `;
  } else {
    paymentNotification = `
      <div class="payment-notification">
        <p>Yth. ${resident.name},</p>
        <p>Sesuai dengan pola bayar sewa per ${resident.paymentPattern} bulan, kami informasikan bahwa periode sewa berikutnya akan jatuh tempo pada ${formatDate(correctDueDate)}. Mohon untuk mempersiapkan pembayaran tepat waktu.</p>
      </div>
    `;
  }

  // HTML for the invoice
  const html = `
    <!DOCTYPE html>
    <html lang="id">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Tagihan Kamar ${resident.roomNumber} - ${resident.name}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          margin: 0;
          padding: 0;
          color: #333;
        }
        .invoice-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 15px;
          border: 1px solid #ddd;
        }
        .invoice-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          border-bottom: 2px solid #0066cc;
          padding-bottom: 8px;
        }
        .invoice-header h1 {
          color: #0066cc;
          margin: 0;
          margin-bottom: 3px;
        }
        .company-address {
          font-size: 11px;
          line-height: 1.3;
          margin-top: 3px;
          margin-bottom: 3px;
        }
        .invoice-details {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }
        .invoice-details-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 3px;
        }
        .customer-details {
          margin-bottom: 15px;
        }
        .customer-details h3 {
          margin-bottom: 5px;
          margin-top: 0;
        }
        .customer-details p {
          margin-top: 3px;
          margin-bottom: 3px;
        }
        .invoice-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 15px;
        }
        .invoice-table th,
        .invoice-table td {
          padding: 8px;
          border-bottom: 1px solid #ddd;
          text-align: left;
        }
        .invoice-table th {
          background-color: #f5f5f5;
        }
        .bank-accounts {
          margin-bottom: 15px;
        }
        .bank-accounts h3 {
          margin-bottom: 5px;
          margin-top: 0;
        }
        .bank-accounts p {
          margin-top: 3px;
          margin-bottom: 8px;
        }
        .bank-accounts-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }
        .bank-account-column {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .bank-account {
          margin-bottom: 0;
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background-color: #f9f9f9;
        }
        .footer-note {
          font-style: italic;
          color: #666;
          margin-top: 8px;
        }
        .footer-note p {
          margin-top: 3px;
          margin-bottom: 3px;
        }
        .status-overdue {
          background-color: #ffebee;
          color: #c62828;
          padding: 3px 8px;
          border-radius: 4px;
          display: inline-block;
          font-weight: bold;
        }
        .status-pending {
          background-color: #fff8e1;
          color: #f57f17;
          padding: 3px 8px;
          border-radius: 4px;
          display: inline-block;
          font-weight: bold;
        }
        .status-on-time {
          background-color: #e8f5e9;
          color: #2e7d32;
          padding: 3px 8px;
          border-radius: 4px;
          display: inline-block;
          font-weight: bold;
        }
        .payment-notification {
          background-color: #f8f9fa;
          border-left: 4px solid #0066cc;
          padding: 10px;
          margin-bottom: 15px;
        }
        .payment-notification p {
          margin-top: 3px;
          margin-bottom: 3px;
        }
        
        @media print {
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .invoice-container {
            border: none;
            max-width: 100%;
            margin: 0;
            padding: 0;
          }
          .status-overdue {
            background-color: #ffebee !important;
            color: #c62828 !important;
          }
          .status-pending {
            background-color: #fff8e1 !important;
            color: #f57f17 !important;
          }
          .status-on-time {
            background-color: #e8f5e9 !important;
            color: #2e7d32 !important;
          }
          .invoice-table th {
            background-color: #f5f5f5 !important;
          }
        }
      </style>
    </head>
    <body>
      <div class="invoice-container">
        <div class="invoice-header">
          <div>
            <h1>deKost SRI 2</h1>
            <p class="company-address">
              Jl. Gajah IV No 6, Kec. Padang Utara, Kota Padang, Sumatera Barat<br>
              Kost Biru, Arah Kampus UNP<br>
              Padang Utara Kota Padang, 25132<br>
              Sumatera Barat<br>
              CP +62 812-7615-1799 (Furqanul Hamdi)
            </p>
            <p>Tagihan Pembayaran Kos</p>
          </div>
          <div>
            <h2>INVOICE</h2>
            <p>No: ${invoiceNumber}</p>
          </div>
        </div>
        
        <div class="invoice-details">
          <div class="invoice-details-row">
            <span>Tanggal Invoice:</span>
            <span>${invoiceDate}</span>
          </div>
          <div class="invoice-details-row">
            <span>Status:</span>
            <span class="${statusClass}">${statusText}</span>
          </div>
        </div>
        
        ${paymentNotification}
        
        <div class="customer-details">
          <h3>Informasi Penghuni:</h3>
          <p>
            ${resident.name}<br>
            Penghuni Kamar ${resident.roomNumber} (Penghuni ${resident.position})<br>
            ${resident.study}<br>
            ${resident.phone}
          </p>
        </div>
        
        <table class="invoice-table">
          <thead>
            <tr>
              <th>Deskripsi</th>
              <th>Periode</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Biaya Sewa Kamar ${resident.roomNumber}</td>
              <td>${formatDate(correctDueDate)} (${resident.paymentPattern} bulan)</td>
              <td class="${statusClass}">${statusText}</td>
            </tr>
          </tbody>
        </table>
        
        <div class="bank-accounts">
          <h3>Informasi Pembayaran:</h3>
          <p>Mohon melakukan pembayaran ke salah satu rekening di bawah ini:</p>
          
          <div class="bank-accounts-grid">
            <div class="bank-account-column">
              ${firstColumnAccounts.map(account => `
                <div class="bank-account">
                  <strong>${account.bankName}</strong><br>
                  No. Rekening: ${account.accountNumber}<br>
                  Atas Nama: ${account.accountHolder}
                </div>
              `).join('')}
            </div>
            <div class="bank-account-column">
              ${secondColumnAccounts.map(account => `
                <div class="bank-account">
                  <strong>${account.bankName}</strong><br>
                  No. Rekening: ${account.accountNumber}<br>
                  Atas Nama: ${account.accountHolder}
                </div>
              `).join('')}
            </div>
          </div>
        </div>
        
        <div class="footer-note">
          <p>Catatan: Harap menghubungi Bang UL (Pengelola) di +62 812-7615-1799 setelah melakukan pembayaran dengan menyertakan bukti transfer.</p>
        </div>
      </div>
    </body>
    </html>
  `;
  
  return html;
}

export function downloadInvoice(resident: Resident, asPdf: boolean = false): void {
  const html = generateInvoiceHTML(resident);
  
  // Open in new window for printing
  const printWindow = window.open('', '_blank');
  
  if (!printWindow) {
    alert('Popup diblokir! Silakan izinkan popup untuk mencetak tagihan.');
    return;
  }
  
  printWindow.document.write(html);
  printWindow.document.close();
  
  // Wait for content to load, then trigger print dialog
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
  };
}